import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { useAuth } from '../../hooks/useAuth';
import { useBarcodeScanner } from '../../hooks/useBarcodeScanner';
import { useWorkerStatus } from '../../hooks/useWorkerStatus';
import operatorService, { ProductionSession } from '../../services/operatorService';
import { wsService } from '../../services/websocket';
import { loadMobileState, clearMobileState } from '../../utils/mobileSessionStorage';
import { normalizeBadgeCode } from '../../utils/barcodeUtils';
import SupervisorOverrideModal from '../Operator/modals/SupervisorOverrideModal';
import ElapsedTimeDisplay from '../Operator/components/ElapsedTimeDisplay';
import toast from 'react-hot-toast';
import {
  Package,
  FileText,
  Clock,
  AlertTriangle,
  LogOut,
  Loader2,
  Info,
  Menu,
  Camera,
  Activity,
  Smartphone,
  ChevronUp,
  ChevronDown,
  Globe,
  ArrowLeft,
} from 'lucide-react';
import CameraScannerModal from '../../components/CameraScannerModal';
import { languageNames, Language } from '../../i18n';
import type { EmployeeInfo, SessionInfo } from '../../types';

interface StationInfo {
  station_id: number;
  station_name: string;
  station_barcode?: string;
}

const MobileOperatorView: React.FC = () => {
  const { t, language, setLanguage } = useLanguage();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Sidebar state
  const [showSideMenu, setShowSideMenu] = useState(false);

  // Get state from navigation, fall back to sessionStorage on refresh
  const navState = location.state as { station?: StationInfo; session?: SessionInfo; employee?: EmployeeInfo } | null;
  const savedState = !navState?.station ? loadMobileState() : null;

  const [station] = useState<StationInfo | null>(navState?.station || savedState?.station as StationInfo || null);
  const [session, setSession] = useState<SessionInfo | null>(navState?.session || savedState?.session as SessionInfo || null);
  const [employee] = useState<EmployeeInfo | null>(navState?.employee || savedState?.employee as EmployeeInfo || null);

  // Real-time session status via WebSocket (for accurate pause state display)
  const { sessionStatus } = useWorkerStatus(station?.station_id, session?.sub_session_number);

  const [isClockingOut, setIsClockingOut] = useState(false);
  const [isTakingBreak, setIsTakingBreak] = useState(false);
  const [showBadgeMenu, setShowBadgeMenu] = useState(false);
  const [stationScannedForMenu, setStationScannedForMenu] = useState(false); // Track if station was scanned for options menu
  const [showScanOutModal, setShowScanOutModal] = useState(false); // Scan out modal
  const [scanOutStep, setScanOutStep] = useState<'station' | 'badge'>('station'); // Track scan out step
  const [showBackConfirm, setShowBackConfirm] = useState(false); // Back to station confirmation
  const [isPackingInfoExpanded, setIsPackingInfoExpanded] = useState(false); // Packing instructions collapse state
  const [showCamera, setShowCamera] = useState(false); // Camera scanner state

  // Supervisor override modal state
  const [showSupervisorModal, setShowSupervisorModal] = useState(false);
  const [supervisorInfo, setSupervisorInfo] = useState<{ id: string; name: string; employee_number: string } | null>(null);
  const [fullSessionData, setFullSessionData] = useState<ProductionSession | null>(null);
  const [isLoadingSupervisor, setIsLoadingSupervisor] = useState(false);

  // If no state, redirect to clock-in
  useEffect(() => {
    if (!station || !session || !employee) {
      navigate('/mobile/clock-in');
    }
  }, [station, session, employee, navigate]);

  // Keep mobile operator view in sync with packer progress/session completion.
  useEffect(() => {
    if (!employee?.employee_number) return;

    let isMounted = true;

    const refreshSession = async (opts?: { closeWhenMissing?: boolean; showCompletedToast?: boolean }) => {
      try {
        const result = await operatorService.getMySession(employee.employee_number);
        const freshSession = result.session;

        if (!isMounted) return;

        if (!freshSession || freshSession.status === 'completed') {
          if (opts?.showCompletedToast) {
            toast.success(t('mobile.sessionCompletedBySupervisor') || 'Session has been completed');
          }

          if (opts?.closeWhenMissing !== false) {
            clearMobileState();
            setSession(null);
            navigate('/mobile/clock-in');
          }
          return;
        }

        setSession(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            started_at: freshSession.started_at || prev.started_at,
            status: freshSession.status || prev.status,
            paused_duration_seconds: freshSession.paused_duration_seconds ?? prev.paused_duration_seconds,
            actual_output_weight: freshSession.actual_output_weight ?? prev.actual_output_weight,
            target_output_weight: freshSession.target_output_weight ?? prev.target_output_weight,
            initial_material_weight: freshSession.initial_material_weight ?? prev.initial_material_weight,
            waste_weight: freshSession.waste_weight ?? prev.waste_weight,
            total_boxes: (freshSession as any).total_boxes ?? prev.total_boxes,
          };
        });
      } catch (err) {
        if (isMounted) {
          console.error('Failed to fetch fresh session data:', err);
        }
      }
    };

    refreshSession({ closeWhenMissing: false });

    wsService.connect();

    const handleRealtimeUpdate = (data: any) => {
      const currentSubSession = session?.sub_session_number;
      const currentSession = session?.session_number;

      const matchesSession =
        data?.sub_session_number === currentSubSession ||
        data?.session_number === currentSession;

      if (!matchesSession) return;

      const showCompletedToast =
        data?.action === 'status_updated' && data?.status === 'completed';

      refreshSession({ showCompletedToast });
    };

    wsService.on('box-packed', handleRealtimeUpdate);
    wsService.on('production-session-updated', handleRealtimeUpdate);
    wsService.on('worker-clocked-in', handleRealtimeUpdate);
    wsService.on('worker-clocked-out', handleRealtimeUpdate);

    return () => {
      isMounted = false;
      wsService.off('box-packed', handleRealtimeUpdate);
      wsService.off('production-session-updated', handleRealtimeUpdate);
      wsService.off('worker-clocked-in', handleRealtimeUpdate);
      wsService.off('worker-clocked-out', handleRealtimeUpdate);
    };
  }, [employee?.employee_number, navigate, session?.session_number, session?.sub_session_number, t]);

  // If no state, show nothing (useEffect will redirect)
  if (!station || !session || !employee) {
    return null;
  }

  // Handle scan - station first, then badge to show options menu or supervisor override
  const handleScan = useCallback(async (barcode: string) => {
    // Check if it's a station barcode scan
    if (barcode.startsWith('#STN-') || barcode.startsWith('#')) {
      // Verify it's the correct station
      const stationBarcode = station?.station_barcode || `#STN-${station?.station_id}`;
      if (barcode === stationBarcode || barcode === `#STN-${station?.station_id}`) {
        setStationScannedForMenu(true);
        toast.success(t('mobile.stationScanned') || 'Station scanned - now scan your badge');
      } else {
        toast.error(t('mobile.wrongStation') || 'Wrong station');
      }
      return;
    }

    // Check if it's a badge scan
    if (barcode.startsWith('@+') || (!barcode.startsWith('#') && !barcode.includes('-'))) {
      if (!stationScannedForMenu) {
        toast.error(t('mobile.scanStationFirst') || 'Please scan station first');
        return;
      }

      const scannedNumber = barcode.replace('@+', '');

      // If same employee as logged in, show regular options menu
      if (scannedNumber === employee.employee_number) {
        setStationScannedForMenu(false);
        setShowBadgeMenu(true);
        return;
      }

      // Different badge scanned - check if it's a supervisor
      setIsLoadingSupervisor(true);
      try {
        const result = await operatorService.lookupEmployee(`@+${scannedNumber}`);

        if (result.success && result.employee?.is_supervisor) {
          // Supervisor badge detected - fetch full session data and open override modal
          setStationScannedForMenu(false);
          setSupervisorInfo({
            id: result.employee.user_id || result.employee.employee_number,
            name: result.employee.display_name,
            employee_number: result.employee.employee_number
          });

          // Fetch full session data for the modal
          try {
            const sessionData = await operatorService.getMySession(employee.user_id || employee.employee_number);
            if (sessionData.session) {
              setFullSessionData(sessionData.session);
              setShowSupervisorModal(true);
              toast.success(t('mobile.supervisorOverrideActivated') || `Supervisor override: ${result.employee.display_name}`);
            } else {
              toast.error(t('mobile.supervisorNoSession') || 'No active session found');
            }
          } catch (err) {
            console.error('Failed to fetch session data:', err);
            toast.error(t('mobile.supervisorSessionError') || 'Failed to load session data');
          }
        } else {
          // Not a supervisor - show error
          toast.error(t('mobile.differentBadge') || 'Different badge scanned');
        }
      } catch (err) {
        console.error('Failed to lookup employee:', err);
        toast.error(t('mobile.supervisorBadgeError') || 'Failed to verify badge');
      } finally {
        setIsLoadingSupervisor(false);
      }
    }
  }, [employee.employee_number, employee.user_id, stationScannedForMenu, station, t]);

  // Listen for station/badge scans
  useBarcodeScanner({
    onScan: handleScan,
    enabled: !showBadgeMenu && !isClockingOut && !showSupervisorModal && !isLoadingSupervisor && !showScanOutModal,
    minLength: 4,
  });

  // Handle clock out
  const handleClockOut = async () => {
    setIsClockingOut(true);
    try {
      const result = await operatorService.clockOut(
        `@+${employee.employee_number}`,
        station.station_id
      );

      if (result.success) {
        clearMobileState();
        toast.success(t('mobile.clockedOut') || 'Clocked out successfully');
        navigate('/mobile/clock-in');
      } else {
        toast.error(result.message || 'Failed to clock out');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to clock out');
    } finally {
      setIsClockingOut(false);
    }
  };

  // Handle take break - pause the session
  const handleTakeBreak = async () => {
    if (!session) return;

    setIsTakingBreak(true);
    try {
      await operatorService.pauseSession(
        session.sub_session_number,
        `Break taken by ${employee?.display_name || employee?.employee_number}`,
        true, // isSubSession
        employee?.employee_number,
        'operator'
      );
      toast.success(t('mobile.breakStarted') || 'Break started - session paused');
      setShowBadgeMenu(false);
      // Navigate back to clock-in since session is paused
      navigate('/mobile/clock-in');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to start break');
    } finally {
      setIsTakingBreak(false);
    }
  };

  // Calculate progress - handle null/undefined/string values
  const actualWeight = parseFloat(String(session.actual_output_weight || 0)) || 0;
  const targetWeight = parseFloat(String(session.target_output_weight || 1)) || 1;
  const initialWeight = parseFloat(String(session.initial_material_weight || 0)) || 0;
  const progress = Math.min(100, (actualWeight / targetWeight) * 100);

  return (
    <div className="min-h-screen flex flex-col bg-gray-100 dark:bg-gray-900">
      {/* Green Header with Hamburger Menu and Session Time */}
      <header className="bg-green-600 dark:bg-green-700 text-white px-4 py-3 sticky top-0 z-10 flex items-center gap-3">
        <button
          onClick={() => setShowSideMenu(true)}
          className="p-2 -ml-2 rounded-lg hover:bg-white/10 active:bg-white/20 transition-colors"
        >
          <Menu className="w-6 h-6" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold">{t('mobile.operator') || 'Operator'}</h1>
          <p className="text-sm opacity-80 truncate">{station.station_name}</p>
        </div>
        {/* Session Time Display - show when session has started */}
        {session.started_at && (
          <ElapsedTimeDisplay
            startedAt={session.started_at}
            isPaused={sessionStatus === 'paused' || session.status === 'paused'}
            pausedDurationSeconds={session.paused_duration_seconds || 0}
          />
        )}
      </header>

      <div className="flex-1 flex flex-col">
        {/* Top Bar - Employee Info */}
        <div className="bg-green-50 dark:bg-green-900/20 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
              {employee.display_name.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white text-sm">{employee.display_name}</p>
              <p className="text-xs text-gray-500">#{employee.employee_number} • Operator</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowCamera(true)}
              className="p-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg"
              title={t('cameraScanner.openCamera') || 'Use Camera'}
            >
              <Camera className="w-5 h-5" />
            </button>
            <button
              onClick={() => setShowBackConfirm(true)}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => {
                setScanOutStep('station');
                setShowScanOutModal(true);
              }}
              disabled={isClockingOut}
              className="p-2 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg"
            >
              {isClockingOut ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogOut className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Packing Instructions Section - Collapsible */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setIsPackingInfoExpanded(!isPackingInfoExpanded)}
            className="w-full px-4 py-3 flex items-center justify-between"
          >
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Package className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
              <div className="flex-1 min-w-0 text-left">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('mobile.productionDetails') || 'Production Details'}
                </h3>
                {!isPackingInfoExpanded && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                    {session.product_sku} · {session.packing || '-'}
                  </p>
                )}
              </div>
            </div>
            <div className="flex-shrink-0 ml-2">
              {isPackingInfoExpanded ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </div>
          </button>
          {isPackingInfoExpanded && (
            <div className="px-4 pb-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <div className="text-gray-500">{t('mobile.itemNumber') || 'Item #'}:</div>
              <div className="text-gray-900 dark:text-white font-medium">{session.product_sku}</div>

              <div className="text-gray-500">{t('mobile.englishName') || 'English'}:</div>
              <div className="text-gray-900 dark:text-white font-medium truncate">{session.product_name}</div>

              {session.product_chinese && (
                <>
                  <div className="text-gray-500">{t('mobile.chineseName') || 'Chinese'}:</div>
                  <div className="text-gray-900 dark:text-white font-medium truncate">{session.product_chinese}</div>
                </>
              )}

              <div className="text-gray-500">{t('mobile.rawMaterial') || 'Raw Material'}:</div>
              <div className="text-gray-900 dark:text-white font-medium">{session.lot_number}</div>

              {session.packing && (
                <>
                  <div className="text-gray-500">{t('mobile.packingSpec') || 'Packing'}:</div>
                  <div className="text-gray-900 dark:text-white font-medium">{session.packing}</div>
                </>
              )}

              {session.expected_boxes !== undefined && session.expected_boxes > 0 && (
                <>
                  <div className="text-gray-500">{t('mobile.expectedBoxes') || 'Expected Boxes'}:</div>
                  <div className="text-green-600 dark:text-green-400 font-bold">{session.expected_boxes}</div>
                </>
              )}

              <div className="text-gray-500">{t('mobile.session') || 'Session'}:</div>
              <div className="text-gray-900 dark:text-white font-medium">{session.sub_session_number}</div>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col p-4 space-y-4">
        {/* Progress Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          {/* Progress Bar */}
          <div className="mb-3">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600 dark:text-gray-400">
                {actualWeight.toFixed(2)} lbs
              </span>
              <span className="font-medium text-gray-900 dark:text-white">
                {targetWeight.toFixed(2)} lbs
              </span>
            </div>
            <div className="h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  progress >= 100 ? 'bg-green-500' : progress >= 80 ? 'bg-amber-500' : 'bg-blue-500'
                }`}
                style={{ width: `${Math.min(100, progress)}%` }}
              />
            </div>
            <p className="text-center text-xs text-gray-500 mt-1">{progress.toFixed(2)}% Complete</p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-2.5 flex flex-col justify-between">
              <div className="text-xs text-gray-500 uppercase">{t('mobile.materialWeight') || 'Material'}</div>
              <div className="text-base font-bold text-gray-900 dark:text-white">
                {initialWeight > 0 ? `${initialWeight.toFixed(2)} lbs` : '-'}
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-2.5 flex flex-col justify-between">
              <div className="text-xs text-gray-500 uppercase">{t('mobile.remaining') || 'Remaining'}</div>
              <div className="text-base font-bold text-gray-900 dark:text-white">
                {Math.max(0, targetWeight - actualWeight).toFixed(2)} lbs
              </div>
            </div>
          </div>
        </div>

        {/* Instructions Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm flex-1">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            {t('mobile.instructions') || 'Instructions'}
          </h3>

          <div className="space-y-3">
            {/* Placeholder instructions - these could come from the session or product */}
            <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {t('mobile.operatorInstructions') || 'Monitor the production line and ensure quality standards are met. Coordinate with packer for box weights.'}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {t('mobile.qualityReminder') || 'Check for foreign materials and ensure proper packaging before sealing.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        </div>
      </div>

      {/* Back to Station Confirmation Modal */}
      {showBackConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 w-full max-w-sm mx-4 rounded-2xl p-6">
            <p className="text-lg font-medium text-center text-gray-900 dark:text-white mb-6">
              {t('mobile.confirmBackToStation') || 'Going back to scan station?'}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowBackConfirm(false)}
                className="flex-1 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold"
              >
                {t('common.no') || 'No'}
              </button>
              <button
                onClick={() => {
                  clearMobileState();
                  navigate('/operator/clock-in');
                }}
                className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-semibold"
              >
                {t('common.yes') || 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scan Out Modal - Station then Badge verification */}
      {showScanOutModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 w-full max-w-md mx-4 rounded-2xl p-6">
            <h3 className="text-lg font-bold text-center text-gray-900 dark:text-white mb-2">
              {t('mobile.scanOut') || 'Scan Out'}
            </h3>
            <p className="text-center text-gray-500 dark:text-gray-400 mb-6">
              {scanOutStep === 'station'
                ? (t('mobile.scanStationBarcode') || 'Scan Station Barcode')
                : (t('mobile.scanYourBadge') || 'Scan Your Badge')
              }
            </p>

            {/* Progress indicator */}
            <div className="flex justify-center gap-2 mb-6">
              <div className={`w-3 h-3 rounded-full ${scanOutStep === 'station' ? 'bg-red-500' : 'bg-green-500'}`} />
              <div className={`w-3 h-3 rounded-full ${scanOutStep === 'badge' ? 'bg-red-500' : 'bg-gray-300 dark:bg-gray-600'}`} />
            </div>

            {/* Scan input */}
            <div className="mb-6">
              <input
                type="text"
                key={scanOutStep} // Force re-render when step changes
                defaultValue={scanOutStep === 'station'
                  ? (station?.station_barcode || `#STN-${station?.station_id}`)
                  : employee.employee_number
                }
                autoFocus
                className="w-full px-4 py-3 text-lg border-2 border-amber-400 dark:border-amber-500 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-center"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const value = e.currentTarget.value.trim();
                    if (!value) return;

                    if (scanOutStep === 'station') {
                      // Verify station barcode matches current station
                      const expectedBarcode = station?.station_barcode || `#STN-${station?.station_id}`;
                      if (value === expectedBarcode || value === `#STN-${station?.station_id}`) {
                        setScanOutStep('badge');
                      } else {
                        toast.error(t('mobile.wrongStation') || 'Wrong station - please scan the correct station');
                      }
                    } else {
                      // Verify badge matches current employee
                      const badgeCode = normalizeBadgeCode(value);
                      const expectedBadge = `@+${employee.employee_number}`;
                      if (badgeCode === expectedBadge) {
                        setShowScanOutModal(false);
                        setScanOutStep('station');
                        setShowBadgeMenu(true);
                      } else {
                        toast.error(t('mobile.wrongBadge') || 'Wrong badge - please scan your badge');
                      }
                    }
                  }
                }}
              />
            </div>

            <button
              onClick={() => {
                setShowScanOutModal(false);
                setScanOutStep('station');
              }}
              className="w-full py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold"
            >
              {t('common.cancel') || 'Cancel'}
            </button>
          </div>
        </div>
      )}

      {/* Badge Options Menu Modal */}
      {showBadgeMenu && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50">
          <div className="bg-white dark:bg-gray-800 w-full max-w-lg rounded-t-2xl p-6 space-y-3 animate-slide-up">
            <h3 className="text-lg font-bold text-center text-gray-900 dark:text-white mb-4">
              {t('mobile.whatWouldYouLikeToDo') || 'What would you like to do?'}
            </h3>

            <button
              onClick={() => {
                setShowBadgeMenu(false);
                handleClockOut();
              }}
              disabled={isClockingOut}
              className="w-full py-4 bg-red-600 text-white rounded-xl font-semibold text-lg flex items-center justify-center gap-3"
            >
              <LogOut className="w-6 h-6" />
              {t('mobile.clockOut') || 'Clock Out'}
            </button>

            <button
              onClick={() => {
                setShowBadgeMenu(false);
                handleTakeBreak();
              }}
              disabled={isTakingBreak}
              className="w-full py-4 bg-amber-500 text-white rounded-xl font-semibold text-lg flex items-center justify-center gap-3 disabled:opacity-50"
            >
              {isTakingBreak ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <Clock className="w-6 h-6" />
              )}
              {isTakingBreak ? (t('mobile.pausing') || 'Pausing...') : (t('mobile.takeBreak') || 'Take Break')}
            </button>

            <button
              onClick={() => setShowBadgeMenu(false)}
              className="w-full py-4 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold text-lg"
            >
              {t('common.cancel') || 'Cancel'}
            </button>
          </div>
        </div>
      )}

      {/* Slide-out Side Menu */}
      {showSideMenu && (
        <div className="fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowSideMenu(false)}
          />
          {/* Menu Panel */}
          <div className="relative w-72 max-w-[80%] bg-white dark:bg-gray-800 h-full shadow-xl flex flex-col">
            {/* Menu Header with Logo */}
            <div className="px-4 py-5 border-b border-gray-200 dark:border-gray-700">
              <img
                src="/wf-removebg-preview.png"
                alt="Way Fong"
                className="h-12 w-auto"
              />
            </div>

            {/* Navigation Items */}
            <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
              {/* Desktop-only navigation items - hidden on mobile */}
              <Link
                to="/operator/production"
                onClick={() => setShowSideMenu(false)}
                className="hidden md:flex items-center px-3 py-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Package className="w-5 h-5 mr-3 text-gray-400" />
                {t('nav.productionEntry') || 'Production Entry'}
              </Link>

              <Link
                to="/operator/performance"
                onClick={() => setShowSideMenu(false)}
                className="hidden md:flex items-center px-3 py-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Activity className="w-5 h-5 mr-3 text-gray-400" />
                {t('dashboard.performance') || 'Performance'}
              </Link>

              <Link
                to="/operator/clock-in"
                onClick={() => setShowSideMenu(false)}
                className="hidden md:flex items-center px-3 py-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Smartphone className="w-5 h-5 mr-3 text-gray-400" />
                {t('nav.mobileView') || 'Mobile View'}
              </Link>

              {/* Divider - only show on desktop when nav links are visible */}
              <div className="my-4 border-t border-gray-200 dark:border-gray-700 hidden md:block" />

              {/* Session Actions */}
              <button
                onClick={() => {
                  setShowSideMenu(false);
                  handleTakeBreak();
                }}
                disabled={isTakingBreak}
                className="w-full flex items-center px-3 py-3 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors disabled:opacity-50"
              >
                {isTakingBreak ? (
                  <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                ) : (
                  <Clock className="w-5 h-5 mr-3" />
                )}
                {isTakingBreak ? (t('mobile.pausing') || 'Pausing...') : (t('mobile.takeBreak') || 'Take Break')}
              </button>

              <button
                onClick={() => {
                  setShowSideMenu(false);
                  handleClockOut();
                }}
                disabled={isClockingOut}
                className="w-full flex items-center px-3 py-3 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
              >
                {isClockingOut ? (
                  <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                ) : (
                  <LogOut className="w-5 h-5 mr-3" />
                )}
                {t('mobile.clockOut') || 'Clock Out'}
              </button>

              {/* Divider before Language */}
              <div className="my-4 border-t border-gray-200 dark:border-gray-700" />

              {/* Language Switcher */}
              <button
                onClick={() => {
                  const langCycle: Language[] = ['en', 'zh-TW', 'es'];
                  const currentIndex = langCycle.indexOf(language);
                  const nextLang = langCycle[(currentIndex + 1) % langCycle.length];
                  setLanguage(nextLang);
                }}
                className="w-full flex items-center px-3 py-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Globe className="w-5 h-5 mr-3 text-gray-400" />
                <span>{t('nav.language') || 'Language'}</span>
                <span className="ml-auto text-sm font-medium text-blue-600 dark:text-blue-400">
                  {languageNames[language]}
                </span>
              </button>
            </nav>

            {/* User Info & Logout at Bottom */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center text-white font-bold">
                  {employee.display_name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white truncate">{employee.display_name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">#{employee.employee_number}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowSideMenu(false);
                  logout();
                }}
                className="w-full py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 flex items-center justify-center gap-2 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                {t('nav.logout') || 'Logout'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Supervisor Override Modal */}
      {fullSessionData && supervisorInfo && (
        <SupervisorOverrideModal
          isOpen={showSupervisorModal}
          onClose={() => {
            setShowSupervisorModal(false);
            setSupervisorInfo(null);
            setFullSessionData(null);
          }}
          supervisorId={supervisorInfo.id}
          supervisorName={supervisorInfo.name}
          supervisorEmployeeNumber={supervisorInfo.employee_number}
          session={fullSessionData}
          operatorEmployeeNumber={employee.employee_number}
          operatorName={employee.display_name}
          onManualWeightToggle={() => {
            // Manual weight toggle is not directly used in MobileOperatorView
            // but required by the modal interface
          }}
        />
      )}

      {/* Camera Scanner Modal — backup for USB barcode scanner */}
      <CameraScannerModal
        isOpen={showCamera}
        onClose={() => setShowCamera(false)}
        onScan={(barcode) => {
          setShowCamera(false);
          handleScan(barcode);
        }}
      />

      {/* Loading overlay when checking supervisor badge */}
      {isLoadingSupervisor && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 flex items-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            <span className="text-gray-700 dark:text-gray-300">
              {t('mobile.verifyingBadge') || 'Verifying badge...'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileOperatorView;
